'use client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { LazyMotion, MotionConfig, type FeatureBundle, type LazyFeatureBundle } from 'motion/react';

import { DEFAULT_EFFECTS_MODE, EFFECTS_STORAGE_KEY, isEffectsMode, resolveEffectsMode, type EffectsMode, type ResolvedEffectsMode } from './effectsPreference';
import { readMotionTokens, type MotionTokens } from './motion';
import { usePersistentState } from './usePersistentState';

interface EffectsValue {
  mode: EffectsMode;
  resolvedMode: ResolvedEffectsMode;
  motionEnabled: boolean;
  ambientMotionEnabled: boolean;
  /** The active design's Motion timing (lib/motion.ts); null only while rendering on the server, where
   *  nothing animates. */
  motion: MotionTokens | null;
  setMode: (mode: EffectsMode) => void;
}

const EffectsContext = createContext<EffectsValue | null>(null);
const loadMotionFeatures = () => import('./motionFeatures').then((module) => module.default);

function readsReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function readBootstrappedMode(): ResolvedEffectsMode {
  try {
    const value = document.documentElement.getAttribute('data-effects');
    return value === 'reduced' || value === 'off' ? value : 'full';
  } catch {
    return 'full';
  }
}

function applyDocumentMode(mode: EffectsMode, resolvedMode: ResolvedEffectsMode) {
  document.documentElement.setAttribute('data-effects-mode', mode);
  document.documentElement.setAttribute('data-effects', resolvedMode);
}

/**
 * Per-device visual-effects preference and the single Motion feature boundary for the web app.
 * `auto` follows the OS reduced-motion preference; explicit modes always win. `off` also disables
 * CSS motion through the root data attribute configured here.
 */
export function EffectsProvider({ children, features = loadMotionFeatures }: {
  children: ReactNode;
  /** Tests can inject the synchronous bundle; production keeps the feature chunk lazy. */
  features?: FeatureBundle | LazyFeatureBundle;
}) {
  const [mode, setModeState, hydrated] = usePersistentState(EFFECTS_STORAGE_KEY, DEFAULT_EFFECTS_MODE, isEffectsMode);
  const [systemReduced, setSystemReduced] = useState(false);
  const [bootstrappedMode] = useState<ResolvedEffectsMode>(readBootstrappedMode);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setSystemReduced(media.matches);
    const onChange = () => setSystemReduced(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  const resolvedMode = hydrated ? resolveEffectsMode(mode, systemReduced) : bootstrappedMode;

  // Read on the client's first render, so the first entrance already runs at the design's pace, and again
  // whenever the document changes skin or effects mode, because both retune the motion tokens.
  const [motion, setMotion] = useState<MotionTokens | null>(() => typeof document === 'undefined' ? null : readMotionTokens(document.documentElement));
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setMotion(readMotionTokens(root)));
    observer.observe(root, { attributes: true, attributeFilter: ['data-skin', 'data-effects'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    applyDocumentMode(mode, resolvedMode);
  }, [hydrated, mode, resolvedMode]);

  const setMode = useCallback((nextMode: EffectsMode) => {
    setModeState(nextMode);
    applyDocumentMode(nextMode, resolveEffectsMode(nextMode, readsReducedMotion()));
  }, [setModeState]);

  const value = useMemo<EffectsValue>(() => ({
    mode,
    resolvedMode,
    motionEnabled: resolvedMode !== 'off',
    ambientMotionEnabled: resolvedMode === 'full',
    motion,
    setMode,
  }), [mode, resolvedMode, motion, setMode]);

  return (
    <EffectsContext.Provider value={value}>
      <LazyMotion features={features} strict>
        <MotionConfig reducedMotion={resolvedMode === 'full' ? 'never' : 'always'}>
          {children}
        </MotionConfig>
      </LazyMotion>
    </EffectsContext.Provider>
  );
}

export function useEffects(): EffectsValue {
  const context = useContext(EffectsContext);
  if (!context) throw new Error('useEffects must be used within EffectsProvider');
  return context;
}
